// WHAT A READ IS WORTH — said in ONE place (design system Appendix B: "one place turns 'in error' into 'unknown'").
//
// A query result has three states that matter, not two, and "not an error" is not "read". A first read that has not answered, the RETRY
// of a first failure (the status goes back to pending, isError false, BEFORE any response), a read paused because the desk is offline —
// every one of them has no data, and a screen that only asked `isError` drew a zero, "square", an empty list ("No such account") or a
// clear to book out of it. Four review rounds found the same hole in a new place each time; this is the one answer they all take.
//
//   'known'   the read SUCCEEDED. A refresh in flight over it does not change that: it is a read, only not the newest one.
//   'waiting' nothing to say yet: pending (a first read, a retry of a failure) or paused (offline). Not an error, and not a read.
//   'failed'  an error — WHATEVER THE CACHE HOLDS. A failed refresh keeps its old rows beside the error, and those rows are not a read.
//
// A GUARD needs 'known'. A BANNER says 'failed' as the failure with its Try again, and 'waiting' as a short "Reading …" line. Nothing is
// claimed from either: no figure, no "square", no "no such party", no "nothing to say".
//
// `balances` — for a party LIST read with its balances: a 200 the BFF flags `balances_read: false` (a ledger list it could not reach) has
// the names and not the balances, and its empty balances are not "square" — for the balances that list is 'failed'.

import { onlineManager } from '@tanstack/react-query';

export type ReadState = 'known' | 'waiting' | 'failed';

/** What a react-query result is asked: three flags and the data, nothing else (so a screen's own stand-in can be passed too). */
export interface ReadResult {
  isError: boolean;
  isSuccess: boolean;
  data?: unknown;
}

export function readState(q: ReadResult, opts: { balances?: boolean } = {}): ReadState {
  if (q.isError) return 'failed';
  if (!q.isSuccess) return 'waiting';
  if (opts.balances && (q.data as { balances_read?: boolean } | null | undefined)?.balances_read === false) return 'failed';
  return 'known';
}

// ── THE FIRST READ AND THE CONNECTION (lifted from the energy lane's screens/Energy.tsx, main 745c87b0) ─────────────────────
// The read client (TanStack v5) starts every page "online" and only listens for the browser's events, so a page opened with no
// connection never hears "offline": its first read is SENT, fails with a connection error (a TypeError from fetch), and the
// screen said "Couldn't read …" — a failure, where there is only a wait. Measured on 27 Oct 2026 by the energy lane: a page
// opened offline read `online: true` from the client and `navigator.onLine === false` from the browser.
// These say what such a read IS. They change words only: a guard still needs 'known', and nothing is enabled by them.

/** This device has no connection — the read client's word (a paused read) or the browser's. */
export function deviceOffline(q?: { fetchStatus?: string }): boolean {
  return q?.fetchStatus === 'paused' || !onlineManager.isOnline() || (typeof navigator !== 'undefined' && navigator.onLine === false);
}

/** A failure that is only the missing connection: a connection error (no answer at all) while this device is offline. Anything
 *  else — the books answered, refused, timed out while online — stays a failure. */
export function offlineFailure(error: unknown): boolean {
  return error instanceof TypeError && deviceOffline();
}

/**
 * THE FIRST READ, while there is nothing to show: 'known' — there is data; 'reading' — asked, no answer yet; 'offline' — waiting
 * for a connection (paused, or failed only because there is none); 'failed' — anything else, red with its way out.
 */
export type FirstRead = 'known' | 'reading' | 'offline' | 'failed';
export function firstRead(q: { isError: boolean; isSuccess: boolean; data?: unknown; error: unknown; fetchStatus: string }): FirstRead {
  if (q.data !== undefined) return 'known';
  if (readState(q) === 'failed') return q.error instanceof TypeError && deviceOffline(q) ? 'offline' : 'failed';
  return deviceOffline(q) ? 'offline' : 'reading';
}
